import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-US");

const AeroTable2 = ({ apiData = [] }) => {
  const pdfRef = useRef(null);

  const computed = useMemo(() => {
    if (!apiData || apiData.length === 0) return null;

    const totalByYear = (year) =>
      apiData.reduce((sum, item) => sum + num(item?.[year]), 0);

    const total2018 = totalByYear("2018");
    const total2024 = totalByYear("2024");

    const growthPercent =
      total2018 > 0
        ? (((total2024 - total2018) / total2018) * 100).toFixed(1)
        : "0.0";

    const maleRow = apiData.find(
      (x) => String(x?.sex_label || "").toLowerCase() === "male",
    );
    const femaleRow = apiData.find(
      (x) => String(x?.sex_label || "").toLowerCase() === "female",
    );

    const male2024 = num(maleRow?.["2024"]);
    const female2024 = num(femaleRow?.["2024"]);

    const malePercent =
      total2024 > 0 ? ((male2024 / total2024) * 100).toFixed(1) : "0.0";
    const femalePercent =
      total2024 > 0 ? ((female2024 / total2024) * 100).toFixed(1) : "0.0";

    // Best growth year (YoY)
    let maxGrowth = -Infinity;
    let bestYear = years[1] || "2019";

    for (let i = 1; i < years.length; i++) {
      const y = years[i];
      const prev = years[i - 1];
      const curTotal = totalByYear(y);
      const prevTotal = totalByYear(prev);
      const g = prevTotal > 0 ? ((curTotal - prevTotal) / prevTotal) * 100 : 0;

      if (g > maxGrowth) {
        maxGrowth = g;
        bestYear = y;
      }
    }

    // Peak year (highest total)
    const totals = years.map((y) => ({ year: y, total: totalByYear(y) }));
    const peak = totals.reduce(
      (best, cur) => (cur.total > best.total ? cur : best),
      totals[0],
    );

    return {
      total2018,
      total2024,
      growthPercent,
      male2024,
      female2024,
      malePercent,
      femalePercent,
      bestYear,
      bestGrowth: maxGrowth,
      peakYear: peak?.year,
      peakTotal: peak?.total,
    };
  }, [apiData]);

  const chartData = useMemo(() => {
    if (!apiData || apiData.length === 0)
      return { labels: years, datasets: [] };

    return {
      labels: years,
      datasets: apiData.map((item) => {
        const isMale = String(item?.sex_label || "").toLowerCase() === "male";
        const isFemale =
          String(item?.sex_label || "").toLowerCase() === "female";

        return {
          label: item?.sex_label || "Unknown",
          data: years.map((y) => num(item?.[y])),
          borderColor: isMale ? "#0ea5e9" : isFemale ? "#EC4899" : "#EC4899",
          backgroundColor: isMale
            ? "#0ea5e980"
            : isFemale
              ? "#EC489980"
              : "#EC489980",
          tension: 0.35,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 5,
        };
      }),
    };
  }, [apiData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { font: { size: 11 }, boxWidth: 12, padding: 10 },
      },
      title: {
        display: true,
        text: "Employment by Gender (2018–2024)",
        font: { size: 16, weight: "bold" },
        padding: { top: 8, bottom: 8 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        grid: { display: false },
        ticks: { font: { size: window.innerWidth < 768 ? 10 : 12 } },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Employees",
          font: { size: window.innerWidth < 768 ? 12 : 14, weight: "bold" },
        },
        ticks: {
          font: { size: window.innerWidth < 768 ? 10 : 12 },
          callback: (v) => fmt(v),
        },
        grid: { display: true },
      },
    },
  };

   const downloadPDF = () => {
     const input = pdfRef.current;
     html2canvas(input, {
       scale: 2,
       useCORS: true,
       backgroundColor: "#ffffff",
       scrollY: -window.scrollY,
     }).then((canvas) => {
       const imgData = canvas.toDataURL("image/png");
       const pdf = new jsPDF("p", "mm", "a4");
       const pageWidth = pdf.internal.pageSize.getWidth();
       const pageHeight = pdf.internal.pageSize.getHeight();
       const imgWidth = pageWidth - 10;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;

       let heightLeft = imgHeight;
       let position = 0;
       let pageNumber = 1;

       const addHeader = () => {
         const logoWidth = 20; // ideal header width
         const logoHeight = 20; // adjust ratio if needed

         pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
         pdf.setFontSize(26);
         pdf.text("UTahData.org", 95, 22);
         pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
         pdf.line(10, 35, 200, 35);
       };

       const addFooter = (pageNumber) => {
         pdf.setFontSize(9);
         const footerY = pageHeight - 10;
         pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
         pdf.text(
           `Downloaded on: ${new Date().toLocaleDateString()}`,
           10,
           footerY,
         );
         pdf.text(
           `Page ${pageNumber}`,
           pageWidth - 10,
           footerY,
           null,
           null,
           "right",
         );
       };

       // First page
       addHeader();
       pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;

       // Add more pages if needed
       while (heightLeft > 0) {
         pdf.addPage();
         pageNumber++;
         addHeader();
         position = heightLeft - imgHeight;
         pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
         heightLeft -= pageHeight - 32;
         addFooter(pageNumber);
       }

       // Final footer
       // ===== FINAL FOOTER SAFE AREA =====

       const footerTopY = pageHeight - 40; // start footer 40mm from bottom
       const footerContentY = pageHeight - 28; // logos row
       const footerBottomTextY = pageHeight - 10; // bottom text row

       // Top Divider Line
       pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

       // UtahData logo
       const utahWidth = 18;
       const utahHeight = 18;
       pdf.addImage(
         utahData,
         "PNG",
         20,
         footerContentY - 8,
         utahWidth,
         utahHeight,
       );

       // Built by
       pdf.setFontSize(10);
       pdf.text("Built by", 45, footerContentY);

       // BODEX logo
       const bodexWidth = 36;
       const bodexHeight = bodexWidth * 0.3;
       pdf.addImage(
         bodex,
         "PNG",
         60,
         footerContentY - 6,
         bodexWidth,
         bodexHeight,
       );

       // Using text
       pdf.text("using", 102, footerContentY);

       // KPI logo
       pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

       // Version
       pdf.text(APP_VERSION, 140, footerContentY);

       // Bottom Info Line
       pdf.setFontSize(8);
       pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         pageWidth - 10,
         footerBottomTextY,
         null,
         null,
         "right",
       );

       pdf.save("employment-by-sex.pdf");
     });
   };

  if (!apiData || apiData.length === 0) return <p>No data available</p>;

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Gender (2018–2024)
        </h2>

        {/* Summary Cards */}
        {computed && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
              <div className="p-4 bg-blue-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Total Growth Since 2018
                </p>
                <p className="text-xl font-bold text-blue-800">
                  {Number(computed.growthPercent) >= 0 ? "+" : ""}
                  {computed.growthPercent}%
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {fmt(computed.total2018)} → {fmt(computed.total2024)}
                </p>
              </div>

              <div className="p-4 bg-yellow-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">Peak Year</p>
                <p className="text-xl font-bold text-yellow-800">
                  {computed.peakYear}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Total: {fmt(computed.peakTotal)}
                </p>
              </div>

              <div className="p-4 bg-green-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Best Growth Year (YoY)
                </p>
                <p className="text-xl font-bold text-green-800">
                  {computed.bestYear} ({computed.bestGrowth >= 0 ? "+" : ""}
                  {computed.bestGrowth.toFixed(1)}%)
                </p>
              </div>
            </div>

            {/* Gender Distribution Cards */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
              <div className="p-4 bg-blue-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Male Employment (2024)
                </p>
                <p className="text-xl font-bold text-blue-800">
                  {fmt(computed.male2024)} ({computed.malePercent}%)
                </p>
              </div>
              <div className="p-4 bg-pink-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Female Employment (2024)
                </p>
                <p className="text-xl font-bold text-pink-800">
                  {fmt(computed.female2024)} ({computed.femalePercent}%)
                </p>
              </div>
            </div> */}
          </>
        )}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Sex</th>
                    {years.map((y) => (
                      <th
                        key={y}
                        className="p-2"
                        style={{ textAlign: "right" }}
                      >
                        {y}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {apiData.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50/60"} hover:bg-gray-50`}
                    >
                      <td className="p-2 text-left font-medium">
                        {item?.sex_label || "Unknown"}
                      </td>
                      {years.map((y) => (
                        <td
                          key={y}
                          className="p-2"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmt(item?.[y])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally to view all years.
            </p> */}
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[420px] w-full h-96 p-4">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable2;

